import type { Amount } from 'stash/types/models/Amount';
import type { TransactionPathType } from 'stash/types/models/Transaction';
import type { PxOrderAccount } from 'stash/types/models/Orders';

export type AutostashResponse = {
    account_id: string;
    auto_stash: Autostash;
    enabled: boolean;
    frequency: string;
    funding_source: string;
    is_running_today: boolean;
    lifetime_saved: number;
    next_execution: string;
    next_execution_formatted: string;
    on: boolean;
    paused: boolean;
    public_id: string;
    total_allocated_amount: string;
    total_allocated_amount_formatted: string;
    status: number;
    success: boolean;
};

export type Autostash = {
    allocations: AutoStashAllocation[];
    enabled: boolean;
    frequency: string;
    funding_source: string;
    is_running_today: boolean;
    lifetime_saved: number;
    next_execution: string;
    next_execution_formatted: string;
    on: boolean;
    paused: boolean;
    public_id: string;
    total_allocated_amount: string;
    total_allocated_amount_formatted: string;
};

export type AutoStashAllocation = {
    amount: string;
    card_id: number;
    card_name: string;
    card_uuid: string;
    formatted_amount: string;
    investment_type: string;
    restricted_reasons: string[];
    ticker_symbol: string;
};

/**
 * String-literal enums emitted verbatim by the px-order automations API
 * (`/apis/orders/v1/automations/...`).
 */
export type AutoStashAutomationStatus =
	| 'AUTOMATION_STATUS_ACTIVE'
	| 'AUTOMATION_STATUS_PAUSED'
	| 'AUTOMATION_STATUS_PAUSED_INDEFINITELY'
	| 'AUTOMATION_STATUS_OFF'
	| 'AUTOMATION_STATUS_UNSPECIFIED';

/**
 * Wire values for the `frequency` field on px-order automations. Defined
 * as a `const` object so callers can reference values by key
 * (`AUTOMATION_FREQUENCY.DAILY`) instead of repeating the string literal.
 */
export const AUTOMATION_FREQUENCY = {
	DAILY: 'AUTOMATION_FREQUENCY_DAILY',
	WEEKLY: 'AUTOMATION_FREQUENCY_WEEKLY',
	BIWEEKLY: 'AUTOMATION_FREQUENCY_BIWEEKLY',
	MONTHLY: 'AUTOMATION_FREQUENCY_MONTHLY',
	UNSPECIFIED: 'AUTOMATION_FREQUENCY_UNSPECIFIED',
} as const;

export type AutoStashAutomationFrequency =
	(typeof AUTOMATION_FREQUENCY)[keyof typeof AUTOMATION_FREQUENCY];

export type AutoStashAutomationDay =
	| 'AUTOMATION_DAY_MONDAY'
	| 'AUTOMATION_DAY_TUESDAY'
	| 'AUTOMATION_DAY_WEDNESDAY'
	| 'AUTOMATION_DAY_THURSDAY'
	| 'AUTOMATION_DAY_FRIDAY'
	| 'AUTOMATION_DAY_SATURDAY'
	| 'AUTOMATION_DAY_SUNDAY'
	| 'AUTOMATION_DAY_UNSPECIFIED';

export type AutoStashAutomationAllocation = {
	cash?: {
		amount: Amount;
	};
	securities?: Array<{
		id?: string;
		tickerSymbol?: string;
		amount?: Amount;
	}>;
};

export type AutoStashScheduledAutomation = {
	id: string;
	source: PxOrderAccount;
	destination: PxOrderAccount;
	status: AutoStashAutomationStatus;
	title: string;
	subtitle: string;
	nextExecutionDate: string;
	frequency: AutoStashAutomationFrequency;
	day: AutoStashAutomationDay;
	amount: Amount;
	allocation: AutoStashAutomationAllocation;
	tags: string[];
};

/**
 * Response shape for
 * `GET /apis/orders/v1/automations/schedules/scheduled`.
 */
export type AutoStashScheduledResponse = {
	automations: AutoStashScheduledAutomation[];
};

/**
 * A single funding source entry returned by
 * `GET /apis/orders/v1/automations/schedules/sources?destinationType=...`.
 *
 * `availableToTransfer` is optional because the same shape is also used for
 * the `source` field on a `PxOrderAccount` returned by
 * `/automations/schedules/scheduled`, which doesn't always include it.
 * Callers must null-check before reading `.value`.
 */
export type AutoStashFundingSourceItem = {
	id: string;
	pathType: TransactionPathType;
	name: string;
	availableToTransfer?: Amount;
};

/**
 * Action the caller can take when no valid funding source exists, e.g.
 * `TRANSFER_PATH_CAPABILITY_TYPE_LINK_EXTERNAL_BANK` → route to bank-link.
 */
export type AutoStashFundingSourceCapability = {
	type: string;
};

/**
 * Response shape for
 * `GET /apis/orders/v1/automations/schedules/sources?destinationType=...`.
 */
export type AutoStashFundingSourcesResponse = {
	sources: AutoStashFundingSourceItem[];
	capabilities?: AutoStashFundingSourceCapability[];
};

/**
 * Response shape for
 * `GET /apis/orders/v1/transfers/ira-match-reward?destinationPathType=...&amountValue=...&amountCurrency=USD`.
 */
export type IRAMatchRewardResponse = {
	iraMatchReward: {
		calculatedAmount?: Amount;
		percentage?: string;
	};
};

/**
 * Request body for `POST /apis/orders/v1/automations/schedules`. Mirrors the
 * px-order automations contract: identifies the user creating the schedule,
 * the funding source / destination accounts, the cadence, the first
 * execution date, and the cash/securities allocation. Securities are
 * optional and only used by self-directed automations; for the Auto Stash
 * V2 cash-allocation flow only `allocation.cash` is set.
 *
 * `startDate` is optional: for an active schedule it's the first execution
 * date, for a paused schedule with a resume date it's the resume date, and
 * for a paused-indefinitely schedule it's omitted entirely.
 */
export type AutoStashCreateScheduleRequest = {
	userId?: string;
	source: {
		id: string;
		pathType: TransactionPathType;
		name?: string;
		availableToTransfer?: Amount;
	};
	destination: {
		id: string;
		pathType: TransactionPathType;
		name?: string;
	};
	frequency: AutoStashAutomationFrequency;
	startDate?: string;
	allocation: {
		cash?: {
			amount: Amount;
		};
		securities?: Array<{
			id?: string;
			tickerSymbol?: string;
			amount?: Amount;
		}>;
	};
	enabled: boolean;
};
